/**
 * Decides how long the full-screen boot loader stays up.
 *
 * HISTORY. This used to release at `ready` as soon as scoped report METADATA
 * arrived, on the reasoning that navigation only needs metadata while the
 * semantic bodies keep loading. That is true of navigation — but the workspace
 * lands on the dashboard, and `DashboardPage` gates on `isSitesLoaded &&
 * isReportsLoaded`, the full bodies. So the loader dismissed at roughly five
 * seconds and handed over to a dashboard that had no data for another thirty,
 * which rendered as an empty page. Measured on a dev server against a real
 * workspace: loader gone at 4.7 s, deck data ready at 39.4 s.
 *
 * The loader normally holds until the data the first screen actually needs
 * is in hand. Once the existing timeout expires, scoped report metadata or
 * bodies let navigation become reachable even if site hydration is pending.
 * This does not mark either collection loaded; the dashboard keeps its own
 * completeness gate until both site and report hydration finish.
 *
 * SCOPE OF `isReportsLoaded`. Since the site-scoped hydration change the flag
 * means "every library row is listed and the OPEN site's current-head bodies
 * are resident", not "every site's bodies are resident". Other sites' bodies
 * are read on demand (`ensureSiteReportBodies`) and the site page combines
 * this flag with its own per-site residency, so the gate releases as soon as
 * the first screen can render rather than after the whole portfolio lands.
 *
 * `allowMetadataRelease` restores the old behaviour for a caller that really
 * does only need navigation. Nothing passes it today; it exists so the
 * metadata-only release is a deliberate opt-in rather than a silent default.
 */
export function workspaceLoaderPhase(input: {
  loading: boolean;
  dataPhase: boolean;
  isSitesLoaded: boolean;
  isReportsLoaded: boolean;
  isReportMetadataLoaded?: boolean;
  allowMetadataRelease?: boolean;
  /**
   * Escape hatch for unfinished workspace hydration. Once the caller's bound
   * elapses, scoped report metadata or bodies release the overlay even while
   * sites are still pending. Authentication and collection loaded flags are
   * unchanged; without either report readiness signal, the overlay still holds.
   */
  bodiesOverdue?: boolean;
}): 'auth' | 'data' | 'ready' {
  if (input.loading && !input.dataPhase) return 'auth';

  const bodiesReady = input.isReportsLoaded;
  const metadataReady = input.isReportMetadataLoaded ?? false;

  // A pending site fetch must not defeat the existing report-ready timeout.
  if (input.bodiesOverdue) return metadataReady || bodiesReady ? 'ready' : 'data';

  if (!input.isSitesLoaded) return 'data';

  if (input.allowMetadataRelease && metadataReady) return 'ready';

  return bodiesReady ? 'ready' : 'data';
}
